export type StreamingMarkdownSplit = {
  frozenBlocks: string[];
  activeBlock: string;
  hasOpenCodeFence: boolean;
};

const CODE_FENCE_LINE_REGEX = /^ {0,3}(`{3,}|~{3,})(.*)$/;

export function splitStreamingMarkdownBlocks({
  source,
  minChunkChars = 0,
}: {
  source: string;
  minChunkChars?: number;
}): StreamingMarkdownSplit {
  if (!source) {
    return {
      frozenBlocks: [],
      activeBlock: '',
      hasOpenCodeFence: false,
    };
  }

  const lines = source.split('\n');
  const rawBlocks: string[] = [];
  let currentLines: string[] = [];
  let openFenceChar: string | null = null;
  let openFenceLength = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    const fenceMatch = CODE_FENCE_LINE_REGEX.exec(line);

    if (fenceMatch) {
      const fenceSeq = fenceMatch[1];
      const fenceRest = fenceMatch[2].trim();
      const char = fenceSeq[0];
      const len = fenceSeq.length;

      if (openFenceChar === null) {
        openFenceChar = char;
        openFenceLength = len;
      } else if (
        char === openFenceChar &&
        len >= openFenceLength &&
        fenceRest.length === 0
      ) {
        openFenceChar = null;
        openFenceLength = 0;
      }
    }

    if (openFenceChar === null && trimmed.length === 0) {
      if (currentLines.length > 0) {
        rawBlocks.push(currentLines.join('\n'));
        currentLines = [];
      }

      continue;
    }

    currentLines.push(line);
  }

  if (currentLines.length > 0) {
    rawBlocks.push(currentLines.join('\n'));
  }

  if (rawBlocks.length <= 1) {
    return {
      frozenBlocks: [],
      activeBlock: source,
      hasOpenCodeFence: openFenceChar !== null,
    };
  }

  const completedRawBlocks = rawBlocks.slice(0, -1);
  const activeBlock = rawBlocks[rawBlocks.length - 1] ?? '';

  if (minChunkChars <= 0) {
    return {
      frozenBlocks: completedRawBlocks,
      activeBlock,
      hasOpenCodeFence: openFenceChar !== null,
    };
  }

  const coalescedBlocks: string[] = [];
  let accumulator = '';

  for (const block of completedRawBlocks) {
    accumulator = accumulator ? `${accumulator}\n\n${block}` : block;

    if (accumulator.length >= minChunkChars) {
      coalescedBlocks.push(accumulator);
      accumulator = '';
    }
  }

  if (accumulator) {
    coalescedBlocks.push(accumulator);
  }

  return {
    frozenBlocks: coalescedBlocks,
    activeBlock,
    hasOpenCodeFence: openFenceChar !== null,
  };
}
